import { extractFacialMeasurements } from './faceDetectionUtils';

/**
 * Classify face shape based on BlazeFace detection
 * @param {Array} faces - Array of faces detected by BlazeFace
 * @returns {string} Classified face shape
 */
export const classifyFaceShape = (faces) => {
  // Extract facial measurements
  const measurements = extractFacialMeasurements(faces);
  
  // If measurements couldn't be calculated, return a default
  if (!measurements) {
    return 'oval';
  }
  
  // Calculate face shape using measurements
  return determineShapeFromMeasurements(measurements);
};

/**
 * Determine face shape from facial measurements
 * @param {Object} measurements - Facial measurements object
 * @returns {string} Determined face shape
 */
export const determineShapeFromMeasurements = (measurements) => {
  const { faceWidth, faceHeight, jawWidth, eyeWidth, cheekboneWidth, foreheadWidth } = measurements;
  
  // Calculate key ratios
  const widthToHeightRatio = faceWidth / faceHeight;
  const jawToFaceWidthRatio = jawWidth / faceWidth;
  const eyeToFaceWidthRatio = eyeWidth / faceWidth;
  const cheekboneToJawRatio = cheekboneWidth / jawWidth;
  const foreheadToJawRatio = foreheadWidth / jawWidth;
  
  // DIAMOND: Narrow forehead and jawline, wide cheekbones
  if (cheekboneToJawRatio > 1.1 && foreheadToJawRatio < 1.1 && jawToFaceWidthRatio < 0.8) {
    return 'diamond';
  }
  
  // HEART: Wider at forehead, narrow at jawline
  if (foreheadToJawRatio > 1.15 && jawToFaceWidthRatio < 0.75) {
    return 'heart';
  }
  
  // ROUND: Width and height are similar with soft angles and full cheeks
  if (widthToHeightRatio > 0.85 && widthToHeightRatio < 1.0 && jawToFaceWidthRatio > 0.78) {
    return 'round';
  }
  
  // SQUARE: Similar width and height with wide jawline
  if (widthToHeightRatio > 0.8 && widthToHeightRatio < 0.95 && jawToFaceWidthRatio > 0.9) {
    return 'square';
  }
  
  // OBLONG: Longer face with similar width at forehead, cheekbones, and jawline
  if (widthToHeightRatio < 0.75 && Math.abs(foreheadToJawRatio - 1.0) < 0.2) {
    return 'oblong';
  }
  
  // OVAL (default): Balanced proportions
  return 'oval';
};

/**
 * Get face shape description
 * @param {string} faceShape - The classified face shape
 * @returns {string} Description of the face shape
 */
export const getFaceShapeDescription = (faceShape) => {
  const descriptions = {
    oval: "The oval face is considered the ideal shape because of its balanced proportions. The forehead is slightly wider than the chin, and the face length is about one and a half times the width.",
    round: "Round face shapes have curved lines with the width and length in similar proportions and no sharp angles. Cheeks are typically the widest part of the face.",
    square: "Square faces have a strong jaw and a broad forehead, with the width and length in similar proportions. The jawline is defined and angular.",
    heart: "Heart-shaped faces have a wider forehead and cheekbones with a narrow jawline and chin. The face typically tapers to a point at the chin.",
    diamond: "Diamond faces have narrow foreheads and jawlines with cheekbones at the widest point. The face has angular features with defined cheekbones.",
    oblong: "Oblong faces are longer than they are wide with a long straight cheek line. The forehead, cheeks, and jawline are similar in width."
  };
  
  return descriptions[faceShape] || "Face shape characteristics could not be determined.";
};

export default {
  classifyFaceShape,
  determineShapeFromMeasurements,
  getFaceShapeDescription
};